import { Component, Input, HostListener } from '@angular/core';
import type { OnChanges, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { faFileDownload, faPenAlt, faPlusCircle, faChevronDown, faWrench, faShieldAlt, faInfoCircle, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { PageContainerService } from '../../services';
import { ReadinessSummaryDto, ReadinessTaskPriorityStatusDto, ReadinessCategoryStatusDto } from '../../data/CommandServiceDtos.generated';
import { categoryDisplayName, formatPercent, gaugeStatus, GaugeStatus } from '../project-summary/summary-ui';

/** One coloured part of the readiness split, matching a segment of the stacked bar. */
export interface ReadinessTooltipRow {
  tone: 'complete' | 'progress' | 'incomplete';
  label: string;
  value: string;
}

@Component({
  standalone: false,
  selector: 'project-summary-readiness',
  templateUrl: './project-summary-readiness.component.html',
  styleUrls: ['./project-summary-readiness.component.scss']
})
export class ProjectSummaryReadinessComponent implements OnInit, OnChanges {
  @Input() readinessSummary: ReadinessSummaryDto;
  @Input() proPakProjectId: number;
  @Input() isActive = true;
  @Input() playToken = 0;
  readinessOverallComplete: number;
  readinessPath: string;
  currentUrl: string;
  faFileDownload = faFileDownload;
  faWorkPackIcon = faWrench;
  faPlusCircle = faPlusCircle;
  faChevronDown = faChevronDown;
  faPencil = faPenAlt;
  faShield = faShieldAlt;
  faInfo = faInfoCircle;
  faFile = faFileAlt;
  proPakProjectUrl: string;
  passThroughAuthKey: string;
  userName: string;
  projectId: number;
  isProjectManager = false;
  status: GaugeStatus = gaugeStatus(0);
  displayPercent = '0%';
  showProPakDropdown = false;

  constructor(
    private pcs: PageContainerService,
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private route: Router
  ) {
    this.userName = pcs.clientSessionService.userName;
  }

  ngOnInit(): void {
    this.isProjectManager = this.pcs.clientSessionService.isInRoleName('Project Manager');
  }

  ngOnChanges() {
    this.projectId = +this.activatedRoute.snapshot.paramMap.get('id');
    this.readinessOverallComplete = this.readinessSummary?.PercentComplete || 0;
    this.readinessPath = this.readinessSummary?.ReadinessFileLocation;
    this.proPakProjectUrl = this.readinessSummary?.ProPakProjectUrl;
    this.passThroughAuthKey = this.readinessSummary?.PassThroughAuthKey;
    this.currentUrl = this.route.url;
    this.status = gaugeStatus(this.readinessOverallComplete);
    this.displayPercent = formatPercent(this.readinessOverallComplete, 1);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target?.closest('.propak-dropdown')) {
      this.showProPakDropdown = false;
    }
  }

  toggleProPakDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showProPakDropdown = !this.showProPakDropdown;
  }

  get hasProgress(): boolean {
    return !!(this.readinessSummary?.Priorities && this.readinessOverallComplete > 0);
  }

  get priorities(): ReadinessTaskPriorityStatusDto[] {
    return this.readinessSummary?.Priorities || [];
  }

  get categoryColumns(): string[] {
    const names: string[] = [];
    for (const priority of this.priorities) {
      for (const category of priority.Categories || []) {
        const display = categoryDisplayName(category.CategoryName);
        if (names.indexOf(display) === -1) {
          names.push(display);
        }
      }
    }
    return names;
  }

  get totalChecks(): number {
    return this.priorities.reduce((sum, priority) => sum + (priority.TaskCountTotal || 0), 0);
  }

  get completeChecks(): number {
    return this.priorities.reduce((sum, priority) => sum + (priority.TaskCountComplete || 0), 0);
  }

  get dimensionCount(): number {
    return this.categoryColumns.length;
  }

  /**
   * The narrowest the table may be drawn; below it the card scrolls instead of squeezing the
   * columns flat. Counts the Status and Count columns, which the stylesheet fixes at 72px and
   * 38px, plus the least a dimension column needs to still show its bar and percentage.
   */
  get tableMinWidth(): number {
    return 72 + 38 + this.categoryColumns.length * 52;
  }

  priorityTone(priority: ReadinessTaskPriorityStatusDto): string {
    const name = (priority.PriorityName || '').toLowerCase();
    if (name.indexOf('high') >= 0) {
      return 'high';
    }
    if (name.indexOf('medium') >= 0) {
      return 'medium';
    }
    return 'low';
  }

  priorityIcon(priority: ReadinessTaskPriorityStatusDto): string {
    const tone = this.priorityTone(priority);
    if (tone === 'high') {
      return 'shield';
    }
    if (tone === 'medium') {
      return 'warning';
    }
    return 'circle';
  }

  categoryFor(priority: ReadinessTaskPriorityStatusDto, displayName: string): ReadinessCategoryStatusDto {
    return (priority.Categories || []).find(category => categoryDisplayName(category.CategoryName) === displayName);
  }

  cellState(priority: ReadinessTaskPriorityStatusDto, displayName: string): 'pass' | 'warn' | 'fail' {
    const category = this.categoryFor(priority, displayName);
    if (!category || !category.TaskCountTotal) {
      return 'fail';
    }
    if (Number(category.PercentComplete) >= 100) {
      return 'pass';
    }
    if (Number(category.PercentComplete) > 0 || Number(category.PercentInProgress) > 0) {
      return 'warn';
    }
    return 'fail';
  }

  categoryPercent(
    priority: ReadinessTaskPriorityStatusDto,
    displayName: string,
    kind: 'complete' | 'inProgress' | 'incomplete'
  ): number {
    const category = this.categoryFor(priority, displayName);
    if (!category) {
      return 0;
    }
    if (kind === 'complete') {
      return Number(category.PercentComplete) || 0;
    }
    if (kind === 'inProgress') {
      return Number(category.PercentInProgress) || 0;
    }
    return Number(category.PercentIncomplete) || 0;
  }

  formatCategoryPercent(priority: ReadinessTaskPriorityStatusDto, displayName: string): string {
    return formatPercent(this.categoryPercent(priority, displayName, 'complete'), 0);
  }

  /**
   * The section's readiness score, scored the same way as the grid's traffic lights, so work that is
   * under way counts towards it. Null when the project scores no columns for this section, in which
   * case the cell falls back to percent complete rather than showing a misleading zero.
   */
  categoryScore(priority: ReadinessTaskPriorityStatusDto, displayName: string): number | null {
    const category = this.categoryFor(priority, displayName);
    if (!category || category.ScorePercent === null || category.ScorePercent === undefined) {
      return null;
    }
    return Number(category.ScorePercent);
  }

  formatCategoryScore(priority: ReadinessTaskPriorityStatusDto, displayName: string): string {
    const score = this.categoryScore(priority, displayName);
    if (score === null) {
      return this.formatCategoryPercent(priority, displayName);
    }
    return formatPercent(score, 0);
  }

  formatPriorityPercent(priority: ReadinessTaskPriorityStatusDto): string {
    return formatPercent(Number(priority.PercentComplete) || 0, 0);
  }

  /**
   * The hovered cell's split, as the three colours the stacked bar is drawn in. Returned as data so
   * the tooltip can colour each part; a plain title attribute renders text only.
   */
  categoryTooltipRows(priority: ReadinessTaskPriorityStatusDto, displayName: string): ReadinessTooltipRow[] {
    return [
      { tone: 'complete', label: 'Completed', value: formatPercent(this.categoryPercent(priority, displayName, 'complete'), 0) },
      { tone: 'progress', label: 'In Progress', value: formatPercent(this.categoryPercent(priority, displayName, 'inProgress'), 0) },
      { tone: 'incomplete', label: 'Not Started', value: formatPercent(this.categoryPercent(priority, displayName, 'incomplete'), 0) }
    ];
  }

  /** The same three colours without percentages, used as a legend for the whole table. */
  get legendRows(): ReadinessTooltipRow[] {
    return [
      { tone: 'complete', label: 'Completed', value: '' },
      { tone: 'progress', label: 'In Progress', value: '' },
      { tone: 'incomplete', label: 'Not Started', value: '' }
    ];
  }

  downloadFile() {
    if (!this.readinessSummary.Project.IsReadinessFormEnabled) {
      if (this.readinessPath === null) {
        this.pcs.notificationService.warning('No Spreadsheet for this Project', '', { timeOut: 3000 });
        return;
      }
      window.open(this.readinessPath, '_blank');
    } else {
      this.ExportReadinessReport();
    }
  }

  async ExportReadinessReport() {
    const response = await this.pcs.apiService.CreateReadinessExcelReport({
      ProjectId: this.projectId,
      TaskExportType: 'both',
      IncludeTaskImages: true,
      IncludeArchivedTaskImages: true,
      ApplyClientTaskFilter: false,
      OrderedExportTasks: []
    });
    if (response && response.ReadinessReport) {
      const byteCharacters = atob(response.ReportData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      this.downloadExportFile(blob, response.FileName);
    }
  }

  private downloadExportFile(blob: Blob, fileName: string): void {
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  launchProPak() {
    this.showProPakDropdown = false;
    window.open(`${this.proPakProjectUrl}#${this.userName},${this.passThroughAuthKey},${this.proPakProjectId}`, '_blank');
  }

  launchAiAssistedProPak() {
    this.showProPakDropdown = false;
    window.open('https://propak.solutions/app/work-packs', '_blank');
  }

  openReadinessForm() {
    this.route.navigate([`/readiness-import/${this.proPakProjectId}`]);
  }
}
